"use client";

import { api } from "@v1/backend/convex/_generated/api";
import { IconOrEmoji } from "@v1/ui/icon-or-emoji";
import { useQuery } from "convex/react";
import { ChevronRight, FileText, Search } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useState } from "react";
import { useMessenger } from "./messenger-context";
import { MessengerDetailHeader, MessengerTabHeader } from "./messenger-header";

const SEARCH_DEBOUNCE_MS = 250;

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState<string>(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [value]);
  return debounced;
}

function PageRow({
  title,
  snippet,
  indent = 0,
  onClick,
}: {
  readonly title: string;
  readonly snippet?: string;
  readonly indent?: number;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        style={indent > 0 ? { paddingLeft: `${1.25 + indent * 1}rem` } : undefined}
        className="flex min-h-11 w-full items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-action-500/[0.04]"
      >
        <FileText className="h-4 w-4 shrink-0 text-primary/30" strokeWidth={1.75} aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[14px] text-primary/85">{title}</span>
          {snippet ? <span className="line-clamp-2 text-[12.5px] leading-snug text-primary/50">{snippet}</span> : null}
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 text-primary/30" strokeWidth={2} aria-hidden />
      </button>
    </li>
  );
}

function SearchResults({ query }: { readonly query: string }): ReactElement {
  const t = useTranslations("messenger.help");
  const { config, nav } = useMessenger();
  const result = useQuery(api.pages.public_help_search.searchPublicHelp, {
    org_slug: config.help_org_slug,
    query,
  });
  if (result === undefined) return <div className="h-24" />;
  if (result.results.length === 0) {
    return <p className="px-6 pt-12 text-center text-[13px] text-primary/45">{t("nichtsGefunden")}</p>;
  }
  return (
    <ul className="divide-y divide-border/40">
      {result.results.map((hit) => (
        <PageRow
          key={hit.page_id}
          title={hit.title}
          snippet={hit.snippet}
          onClick={() => nav.push({ kind: "article", pageId: hit.page_id })}
        />
      ))}
    </ul>
  );
}

function Spaces(): ReactElement {
  const t = useTranslations("messenger.help");
  const { config, nav } = useMessenger();
  const center = useQuery(api.pages.public_help.getPublicHelpCenter, { org_slug: config.help_org_slug });
  if (center === undefined) return <div className="h-24" />;
  if (center.state !== "ok" || center.spaces.length === 0) {
    return <p className="px-6 pt-12 text-center text-[13px] text-primary/45">{t("keineSeiten")}</p>;
  }
  return (
    <div className="grid gap-3 p-5 sm:grid-cols-2">
      {center.spaces.map((space) => (
        <button
          key={space.space_id}
          type="button"
          onClick={() => nav.push({ kind: "helpSpace", spaceId: space.space_id })}
          className="flex items-start gap-3 rounded-xl border border-border/60 bg-background px-4 py-3.5 text-left transition-colors hover:border-border hover:bg-action-500/[0.03]"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-action-500/[0.08] text-action-700">
            <IconOrEmoji value={space.icon ?? undefined} fallback="📘" size={16} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[14px] font-semibold text-primary/90">{space.name}</span>
            {space.description ? (
              <span className="line-clamp-2 text-[12.5px] leading-snug text-primary/50">{space.description}</span>
            ) : null}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Hilfe tab: search over the public help pages, else the spaces. */
export function MessengerHelpView(): ReactElement {
  const t = useTranslations("messenger");
  const [text, setText] = useState<string>("");
  const query = useDebounced(text.trim());
  return (
    <>
      <MessengerTabHeader title={t("tabs.hilfe")} />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4">
        <div className="sticky top-0 z-10 bg-background px-5 pb-2 pt-4">
          <label className="flex h-11 items-center gap-2 rounded-xl bg-action-500/[0.05] px-3.5 focus-within:ring-1 focus-within:ring-primary/20">
            <Search className="h-4 w-4 shrink-0 text-primary/40" strokeWidth={2} aria-hidden />
            <input
              type="text"
              enterKeyHint="search"
              // biome-ignore lint/a11y/noAutofocus: the Hilfe tab opens for typing a search
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("help.suchen")}
              aria-label={t("help.suchen")}
              className="min-w-0 flex-1 bg-transparent text-[16px] text-primary/90 placeholder:text-primary/40 focus:outline-none md:text-[14px]"
            />
          </label>
        </div>
        {query.length >= 2 ? <SearchResults query={query} /> : <Spaces />}
      </div>
    </>
  );
}

/** One space: its public pages as a tree list. */
export function MessengerHelpSpaceView({ spaceId }: { readonly spaceId: string }): ReactElement {
  const { config, nav } = useMessenger();
  const center = useQuery(api.pages.public_help.getPublicHelpCenter, { org_slug: config.help_org_slug });
  const space = center?.state === "ok" ? center.spaces.find((s) => s.space_id === spaceId) : undefined;
  return (
    <>
      <MessengerDetailHeader title={space?.name ?? ""} />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-2">
        <ul>
          {(space?.pages ?? []).map((page) => (
            <PageRow
              key={page.page_id}
              title={page.title}
              indent={Math.min(page.depth, 3)}
              onClick={() => nav.push({ kind: "article", pageId: page.page_id })}
            />
          ))}
        </ul>
      </div>
    </>
  );
}
